import { PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { v4 as uuidv4 } from 'uuid'
import {
  JobStatusType,
  ImportingActionPayload,
  ImportingJob,
  ImportingJobResponse,
  ImportingJobPayload
} from '../types'
import {
  addImportingJob,
  createImportingJobRequest,
  getImportingJobsRequest,
  removeImportingJob,
  setCurrentImportingJob,
  setImportingJobs,
  setImportingJobsStatusRequest,
  updateImportingJobStatus
} from './jobsImportingSlice'
import { checkImportJobHasFinished, createImportingJob, getImportingJobsList } from '../api/importJobs'
import {
  reloadDataTreeItemRequest,
  setCurrentTable,
  setCurrentTableDataPreview,
  setCurrentTableDataPreviewStatus,
  setCurrentTableError,
  setCurrentTableStatus,
  setCurrentTileset,
  setCurrentTilesetStats,
  setCurrentTilesetStatsStatus,
  setCurrentTilesetStatus
} from 'features/data-explorer/state/dataExplorerSlice'
import { RequestStatus } from 'features/common'
import { Connection } from 'features/connections'
import { addLayerFromImportedJobRequest } from 'features/builder/state/builderSlice'
import { DataExplorerTable } from 'features/data-explorer/types'
import { pollJobUntilFinished } from './jobsSagas'
import { queryClient } from 'queryClient'
import { DATA_EXPLORER_KEYS } from 'features/new-data-explorer/api/dataExplorerQueries'

function* createImportingJobAndPolling(action: PayloadAction<ImportingActionPayload>) {
  const {
    connection,
    destination,
    name,
    file,
    createLayerAfterJobImportedForMap,
    mapId,
    autoguessing,
    metadata,
    source,
    schema
  } = action.payload
  const provisionalJobId = uuidv4()

  try {
    const user_id = yield select((state) => state.users.me?.user_id)
    let importingJob: ImportingJob = {
      jobId: provisionalJobId,
      connection,
      destination,
      name,
      status: JobStatusType.Pending,
      metadata,
      source
    }

    yield put(setCurrentImportingJob(importingJob))
    // A provisional importingJob is added to the store to be able to show the
    // job status in the interface without having to wait for createImportingJob request that has to upload the entire file to finish
    yield put(addImportingJob(importingJob))

    importingJob = yield call(createImportingJob, {
      provisionalJobId,
      user_id,
      file,
      connection,
      destination,
      name,
      autoguessing,
      metadata,
      source,
      schema
    })
    importingJob = { ...importingJob, mapId, createLayerAfterJobImportedForMap }

    const currentImportingJob: ImportingJob | undefined = yield select(
      (state) => state.jobsImporting.currentImportingJob
    )
    // It could happend that while we're waiting for createImportingJob response the user starts
    // another import proccess. In that case the currentImportingJob would have changed
    if (currentImportingJob?.jobId === provisionalJobId) {
      yield put(setCurrentImportingJob(importingJob))
    }

    // Is important to execute the addImportingJob action before the removeImportingJob action for workflows
    yield put(addImportingJob(importingJob))
    yield put(removeImportingJob(provisionalJobId))
    yield pollingImportingJobsList([importingJob])
  } catch (e) {
    yield put(
      setCurrentImportingJob({
        jobId: provisionalJobId,
        name,
        connection,
        destination,
        status: JobStatusType.Failure,
        error: (e as Error).message,
        metadata,
        source
      })
    )
    yield put(removeImportingJob(provisionalJobId))
  }
}

function* pollingImportingJobsList(jobs: ImportingJob[]) {
  try {
    yield all(
      jobs.map((job) =>
        call(function* () {
          const { status, error } = yield pollJobUntilFinished(() => checkImportJobHasFinished({ jobId: job.jobId }))

          const jobResult = { ...job, status, error }
          const currentImportingJob: ImportingJob | undefined = yield select(
            (state) => state.jobsImporting.currentImportingJob
          )

          if (currentImportingJob && jobResult.jobId === currentImportingJob.jobId) {
            yield put(setCurrentImportingJob(jobResult))
          }
          if (jobResult.status === JobStatusType.Success) {
            const connection: Connection = yield select((state) =>
              state.connections.myConnections.find((c) => c.name === job.connection)
            )
            if (connection) {
              yield put(
                reloadDataTreeItemRequest({
                  connectionId: connection.id,
                  provider_id: connection.provider_id,
                  items: job.destination.split('.')
                })
              )
              // This is to refetch the resource in the new data explorer
              queryClient.refetchQueries([...DATA_EXPLORER_KEYS.resources, connection.id, job.destination, null])

              const currentConnection: Connection | null = yield select((state) => state.connections.currentConnection)
              const currentTable: DataExplorerTable | null = yield select((state) => state.dataExplorer.currentTable)
              const importedTableId = `${job.destination}.${job.name}`

              // update dataexplorer preview if needed
              if (currentConnection?.id === connection.id && currentTable?.id === importedTableId) {
                yield put(setCurrentTileset(null))
                yield put(setCurrentTilesetStatus(RequestStatus.Idle))
                yield put(setCurrentTilesetStats(null))
                yield put(setCurrentTilesetStatsStatus(RequestStatus.Idle))
                yield put(setCurrentTable(null))
                yield put(setCurrentTableError(null))
                yield put(setCurrentTableDataPreview(null))
                yield put(setCurrentTableDataPreviewStatus(RequestStatus.Idle))
                yield put(setCurrentTableStatus(RequestStatus.Idle))
              }
            }
            if (jobResult.mapId) {
              yield put(addLayerFromImportedJobRequest(jobResult))
            }
          }
          yield put(updateImportingJobStatus(jobResult))
        })
      )
    )
  } catch (e) {
    throw new Error(e as string)
  }
}

function* getImportingJobs(action: PayloadAction<ImportingJobPayload>) {
  try {
    yield put(setImportingJobsStatusRequest(RequestStatus.Pending))
    const importingJobsFetched: ImportingJobResponse[] = yield call(getImportingJobsList, action.payload)

    const importingJobs = importingJobsFetched.map((job) => {
      const destination = job.destination.split('.')
      const name = destination.pop()
      return {
        jobId: job.jobId,
        name: name || '',
        connection: job.connection,
        createdAt: job.createdAt,
        destination: destination.join('.'),
        status: job.status,
        error: job.error,
        metadata: job.metadata,
        source: job.source
      }
    })
    yield put(setImportingJobs(importingJobs))
    yield put(setImportingJobsStatusRequest(RequestStatus.Fulfilled))
    const pendingJobs = importingJobs.filter(
      (job) => job.status === JobStatusType.Pending || job.status === JobStatusType.Running
    )
    yield pollingImportingJobsList(pendingJobs)
  } catch (e) {
    console.error(e)
  }
}

function* watchFetchJobs() {
  yield takeEvery(createImportingJobRequest.type, createImportingJobAndPolling)
  yield takeEvery(getImportingJobsRequest.type, getImportingJobs)
}

export default function* jobsImportingSaga() {
  try {
    yield all([watchFetchJobs()])
  } catch (e) {
    //TODO
  }
}
